import React, { useState } from 'react';
import { useSampleCart } from '../contexts/SampleCartContext';
import { X, Trash, ShoppingCart, PaperPlaneTilt, CheckCircle, SpinnerGap } from '@phosphor-icons/react';
import { motion, AnimatePresence } from '@/lib/motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function SampleCartDrawer() {
  const { items, removeItem, clearCart, isOpen, closeDrawer } = useSampleCart();
  const [step, setStep] = useState('cart'); // cart | form | success
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', project_name: '', shipping_address: '', city: '', country: 'UAE', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setError('Name and email are required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/sample-requests/batch`, {
        items: items.map(i => ({ finish_id: i.id, finish_name: i.name, finish_code: i.code, category: i.category })),
        ...form,
      });
      setStep('success');
      setTimeout(() => { clearCart(); setStep('cart'); closeDrawer(); }, 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-obsidian/40 backdrop-blur-sm" onClick={closeDrawer} />
          <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 250 }}
            className="fixed right-0 top-0 bottom-0 z-[61] w-full max-w-md bg-white shadow-2xl flex flex-col" data-testid="sample-cart-drawer">

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <div className="flex items-center gap-2">
                <ShoppingCart weight="fill" className="w-5 h-5 text-bronze" />
                <h2 className="font-display text-lg font-bold uppercase">Sample Cart</h2>
                <span className="px-2 py-0.5 bg-bronze text-obsidian text-[10px] font-bold">{items.length}</span>
              </div>
              <button onClick={closeDrawer} className="p-2 hover:bg-gallery transition-colors" data-testid="close-cart"><X weight="bold" className="w-5 h-5" /></button>
            </div>

            {step === 'success' ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                  <CheckCircle weight="fill" className="w-16 h-16 text-bronze mx-auto mb-4" />
                  <h3 className="font-display text-xl font-bold mb-2">Request Submitted</h3>
                  <p className="text-sm text-concrete">Our team will prepare your samples and reach out within 24 hours.</p>
                </div>
              </div>
            ) : step === 'form' ? (
              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
                <p className="text-xs text-concrete uppercase tracking-wider mb-2">Requesting {items.length} finish sample{items.length > 1 ? 's' : ''}</p>
                {/* Mini swatch previews */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {items.map(i => (
                    <div key={i.id} className="w-8 h-8 border border-black/10 overflow-hidden" title={i.name}>
                      {i.image ? <img src={i.image} alt={i.name} className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ backgroundColor: i.colorHex }} />}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-concrete block mb-1">Full Name *</label>
                    <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-black/10 focus:border-obsidian focus:outline-none" data-testid="cart-name" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-concrete block mb-1">Email *</label>
                    <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-black/10 focus:border-obsidian focus:outline-none" data-testid="cart-email" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-concrete block mb-1">Phone</label>
                    <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-black/10 focus:border-obsidian focus:outline-none" data-testid="cart-phone" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-concrete block mb-1">Company</label>
                    <input type="text" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-black/10 focus:border-obsidian focus:outline-none" data-testid="cart-company" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-concrete block mb-1">Project Name</label>
                    <input type="text" value={form.project_name} onChange={e => setForm({ ...form, project_name: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-black/10 focus:border-obsidian focus:outline-none" data-testid="cart-project" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-concrete block mb-1">Shipping Address</label>
                    <input type="text" value={form.shipping_address} onChange={e => setForm({ ...form, shipping_address: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-black/10 focus:border-obsidian focus:outline-none" data-testid="cart-address" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-concrete block mb-1">City</label>
                    <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-black/10 focus:border-obsidian focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-concrete block mb-1">Country</label>
                    <select value={form.country} onChange={e => setForm({ ...form, country: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-black/10 focus:border-obsidian focus:outline-none bg-white">
                      {['UAE', 'Saudi Arabia', 'Qatar', 'Bahrain', 'Oman', 'Kuwait', 'India', 'UK', 'USA', 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-concrete block mb-1">Notes</label>
                    <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                      className="w-full px-3 py-2.5 text-sm border border-black/10 focus:border-obsidian focus:outline-none resize-none" />
                  </div>
                </div>
                {error && <p className="text-xs text-red-500">{error}</p>}
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setStep('cart')} className="px-4 py-3 border border-black/10 text-xs font-bold uppercase tracking-wider hover:border-obsidian transition-colors">Back</button>
                  <button type="submit" disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-obsidian text-white text-xs font-bold uppercase tracking-wider hover:bg-bronze hover:text-obsidian transition-colors disabled:opacity-50"
                    data-testid="cart-submit">
                    {submitting ? <SpinnerGap className="w-4 h-4 animate-spin" /> : <PaperPlaneTilt className="w-4 h-4" />}
                    {submitting ? 'Submitting...' : 'Request Samples'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto">
                  {items.length === 0 ? (
                    <div className="p-8 text-center">
                      <ShoppingCart className="w-12 h-12 text-black/10 mx-auto mb-3" />
                      <p className="text-sm text-concrete">Your sample cart is empty</p>
                      <p className="text-xs text-concrete mt-1">Browse finishes and add samples to request</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-black/5">
                      {items.map(item => (
                        <div key={item.id} className="flex items-center gap-3 p-3 hover:bg-gallery/30 transition-colors" data-testid={`cart-item-${item.id}`}>
                          <div className="w-12 h-12 flex-shrink-0 border border-black/10 overflow-hidden">
                            {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full" style={{ backgroundColor: item.colorHex }} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-obsidian truncate">{item.name}</p>
                            <p className="text-[10px] text-concrete uppercase tracking-wider">{item.category}{item.code ? ` - ${item.code}` : ''}</p>
                          </div>
                          <button onClick={() => removeItem(item.id)} className="p-1.5 text-concrete hover:text-red-500 transition-colors" data-testid={`remove-${item.id}`}>
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                {items.length > 0 && (
                  <div className="border-t border-black/5 p-4 space-y-2">
                    <button onClick={() => setStep('form')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-obsidian text-white text-xs font-bold uppercase tracking-wider hover:bg-bronze hover:text-obsidian transition-colors"
                      data-testid="cart-checkout">
                      <PaperPlaneTilt className="w-4 h-4" /> Request {items.length} Sample{items.length > 1 ? 's' : ''}
                    </button>
                    <button onClick={clearCart} className="w-full px-4 py-2 text-xs text-concrete hover:text-obsidian uppercase tracking-wider transition-colors" data-testid="cart-clear">
                      Clear Cart
                    </button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Floating cart button
export function SampleCartButton() {
  const { items, toggleDrawer } = useSampleCart();

  return (
    <AnimatePresence>
      {items.length > 0 && (
        <motion.button
          key="sample-cart-fab"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          onClick={toggleDrawer}
          className="fixed bottom-24 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-obsidian text-white shadow-xl hover:bg-bronze hover:text-obsidian transition-colors"
          data-testid="sample-cart-fab"
        >
          <ShoppingCart weight="fill" className="w-5 h-5" />
          <span className="text-xs font-bold uppercase tracking-wider">Samples</span>
          <span className="w-5 h-5 flex items-center justify-center bg-bronze text-obsidian text-[10px] font-bold">{items.length}</span>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
